import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'reactstrap';
import { MediaResponse } from 'types';
import Media from '../../../components/media/Media';
import ConfirmationDialogModal from '../Modals/ConfirmationDialogModal';
import MediaUploader from './MediaUploader';

interface Props {
  onChange: (value?: string) => void;
  value: string;
  callToAction?: string;
  uploadUrl?: string;
  multiple?: boolean;
  enableDescriptionField?: boolean;
  descriptionFieldPlaceholder?: string;
  mediaResponseMapper?: (mediaResponse: MediaResponse) => string;
  maxFiles?: number;
  maxFileSizeMb?: number;
  required?: boolean;
  onValidChange?: (validationMessage: string | null) => void;
}

const DEFAULT_MEDIA_RESPONSE_MAPPER = (mediaResponse: MediaResponse) =>
  mediaResponse.link;

const MediaUploaderInput = ({
  value,
  onChange,
  callToAction,
  uploadUrl,
  multiple = false,
  enableDescriptionField = false,
  maxFiles,
  maxFileSizeMb,
  descriptionFieldPlaceholder,
  mediaResponseMapper = DEFAULT_MEDIA_RESPONSE_MAPPER,
  required = false,
  onValidChange,
}: Props) => {
  const { formatMessage } = useIntl();
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (required) {
      setValidationMessage(
        value
          ? null
          : formatMessage({
              id: 'app.views.widgets.inputs.media_uploader_input.validation.required',
              defaultMessage: 'Please upload a file.',
            })
      );
    } else {
      setValidationMessage(null);
    }
  }, [value, required, formatMessage]);

  useEffect(() => {
    if (onValidChange) {
      onValidChange(validationMessage);
    }
  }, [validationMessage, onValidChange]);

  const handleClose = useCallback(() => {
    setConfirmDeleteModalOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    setConfirmDeleteModalOpen(!confirmDeleteModalOpen);
  }, [confirmDeleteModalOpen]);

  const handleCallback = useCallback(() => {
    onChange(undefined);
    setConfirmDeleteModalOpen(false);
  }, [onChange]);

  const handleOpen = useCallback(() => {
    setConfirmDeleteModalOpen(true);
  }, []);

  const handleOnUploadedFilesChanged = useCallback(
    (files) => {
      onChange(files ? files.map(mediaResponseMapper) : undefined);
    },
    [mediaResponseMapper, onChange]
  );

  if (multiple) {
    return (
      <MediaUploader
        onUploadedChange={handleOnUploadedFilesChanged}
        uploadUrl={uploadUrl}
        enableDescriptionField={enableDescriptionField}
        maxFiles={maxFiles}
        maxFileSizeMb={maxFileSizeMb}
        callToAction={
          callToAction ??
          formatMessage({
            id: 'app.views.widgets.inputs.media_uploader_input.call_to_action',
            defaultMessage: 'Add image or video',
          })
        }
      />
    );
  }

  return (
    <>
      {value && <Media link={value} />}
      {!value && (
        <MediaUploader
          onUpload={(file) => onChange(mediaResponseMapper(file))}
          uploadUrl={uploadUrl}
          maxFileSizeMb={maxFileSizeMb}
          enableDescriptionField={enableDescriptionField}
          descriptionFieldPlaceholder={
            descriptionFieldPlaceholder ??
            formatMessage({
              id: 'app.views.widgets.inputs.media_uploader_input.description_field_placeholder',
              defaultMessage: 'Add a description for the file',
            })
          }
          callToAction={
            callToAction ??
            formatMessage({
              id: 'app.views.widgets.inputs.media_uploader_input.call_to_action',
              defaultMessage: 'Add image or video',
            })
          }
        />
      )}
      {value && (
        <Button className="btn btn-white" onClick={handleOpen}>
          <FormattedMessage
            id="app.views.widgets.inputs.media_uploader_input.delete"
            defaultMessage="Delete"
          />
        </Button>
      )}
      <ConfirmationDialogModal
        isOpen={confirmDeleteModalOpen}
        onClosed={handleClose}
        toggle={handleToggle}
        confirmCallback={handleCallback}
        title={formatMessage({
          id: 'app.views.widgets.inputs.media_uploader_input.modal.title.delete_media',
          defaultMessage: 'Delete media',
        })}
        description={formatMessage({
          id: 'app.views.widgets.inputs.media_uploader_input.modal.description.delete_media',
          defaultMessage: 'Are you sure that you want to delete this media?',
        })}
        confirmText={formatMessage({
          id: 'app.views.widgets.inputs.media_uploader_input.modal.confirm_text.delete_media',
          defaultMessage: 'Delete',
        })}
      />
    </>
  );
};

export default MediaUploaderInput;
